/**
 * Storage utility - équivalent de GetStorage dans Flutter
 * Provides a simple key-value storage using localStorage with type safety
 */

class Storage {
  private prefix = 'mobcash_'

  /**
   * Write a value to storage
   */
  write<T>(key: string, value: T): void {
    try {
      const serialized = JSON.stringify(value)
      localStorage.setItem(this.prefix + key, serialized)
    } catch (error) {
      console.error(`Error writing to storage (${key}):`, error)
    }
  }

  /**
   * Read a value from storage
   */
  read<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(this.prefix + key)
      if (item === null) return null
      return JSON.parse(item) as T
    } catch (error) {
      console.error(`Error reading from storage (${key}):`, error)
      return null
    }
  }

  /**
   * Remove a value from storage
   */
  remove(key: string): void {
    try {
      localStorage.removeItem(this.prefix + key)
    } catch (error) {
      console.error(`Error removing from storage (${key}):`, error)
    }
  }

  /**
   * Check if a key exists in storage
   */
  hasData(key: string): boolean {
    return localStorage.getItem(this.prefix + key) !== null
  }

  /**
   * Clear all storage (with prefix)
   */
  clear(): void {
    try {
      const keys = Object.keys(localStorage)
      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          localStorage.removeItem(key)
        }
      })
    } catch (error) {
      console.error('Error clearing storage:', error)
    }
  }

  /**
   * Get all keys in storage
   */
  getKeys(): string[] {
    const keys: string[] = []
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(this.prefix)) {
          keys.push(key.replace(this.prefix, ''))
        }
      }
    } catch (error) {
      console.error('Error getting keys:', error)
    }
    return keys
  }

  /**
   * Listen to storage changes (for cross-tab communication)
   */
  listen(callback: (key: string, value: any) => void): () => void {
    const handler = (e: StorageEvent) => {
      if (e.key && e.key.startsWith(this.prefix)) {
        const key = e.key.replace(this.prefix, '')
        const value = e.newValue ? JSON.parse(e.newValue) : null
        callback(key, value)
      }
    }

    window.addEventListener('storage', handler)

    // Return cleanup function
    return () => {
      window.removeEventListener('storage', handler)
    }
  }
}

// Create singleton instance
export const storage = new Storage()

// Initialize storage (like GetStorage.init() in Flutter)
export const initStorage = async (): Promise<void> => {
  // LocalStorage is synchronous and always available in browser
  // This function exists for API compatibility with Flutter GetStorage
  return Promise.resolve()
}
